import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { Keyboard } from '@capacitor/keyboard';
import { Pwa } from '@freelancer/pwa';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { startWith } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class KeyboardService implements OnDestroy {
  private pwa = inject(Pwa);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);

  private _isOpenSubject$ = new Subject<boolean>();
  private listenersAdded = false;

  isOpen(): Observable<boolean> {
    if (!this.listenersAdded && this.pwa.isNative()) {
      this.addListeners();
    }

    return this._isOpenSubject$.asObservable().pipe(startWith(false));
  }

  async addListeners(): Promise<void> {
    Keyboard.addListener('keyboardWillShow', () => {
      this.ngZone.run(() => {
        this._isOpenSubject$.next(true);
      });
    });

    Keyboard.addListener('keyboardDidHide', () => {
      this.ngZone.run(() => {
        this._isOpenSubject$.next(false);
      });
    });

    this.listenersAdded = true;
  }

  async setAccessoryBarVisible(isVisible: boolean): Promise<void> {
    if (this.pwa.getPlatform() === 'ios') {
      Keyboard.setAccessoryBarVisible({ isVisible });
    }
  }

  /** Dismisses the soft keyboard on native app. */
  hide(): void {
    if (isPlatformBrowser(this.platformId) && this.pwa.isNative()) {
      Keyboard.hide();
    }
  }

  async removeListeners(): Promise<void> {
    Keyboard.removeAllListeners();
  }

  ngOnDestroy(): void {
    if (this.listenersAdded && this.pwa.isNative()) {
      this.removeListeners();
    }
  }
}
